class RebuyClient {
  constructor({ clientToken }) {
    this.maxCacheMinutes = 30;
    this.storageKey = 'productJSON';
    this.clientToken = clientToken;
  }

  async query(url, params) {
    return fetch(
      `https://rebuyengine.com/${url}?${new URLSearchParams({
        key: this.clientToken,
        ...params,
      })}`,
      {
        method: 'GET',
        headers: {
          'Content-type': 'application/json; charset=UTF-8',
        },
      }
    )
      .then((res) => res.json())
      .then((res) => {
        return res;
      });
  }

  async getProductsByIDs(productIDs) {
    const products = [];
    const staticIDs = [];

    productIDs.forEach((productID) => {
      const cachedProduct = this.getProductFromCacheByID(productID);

      if (cachedProduct) {
        products.push(cachedProduct);
      } else {
        staticIDs.push(productID);
      }
    });

    if (staticIDs.length > 0) {
      const response = await this.query('api/v1/products/static', {
        metafields: 'yes',
        shopify_product_ids: staticIDs.join(','),
        limit: 100,
      });

      response.data.forEach((product) => {
        this.saveProductToCacheByID(product.id, product);
      });

      products.push(...response.data);
    }

    return products.sort((a, b) => {
      return productIDs.indexOf(a.id) - productIDs.indexOf(b.id);
    });
  }

  async getProductByID(productID) {
    const cachedProduct = this.getProductFromCacheByID(productID);

    if (cachedProduct) {
      return cachedProduct;
    }

    console.log('Calling RebuyEngine API for product ID:', productID);

    const response = await this.query('api/v1/products/static', {
      metafields: 'yes',
      shopify_product_ids: productID,
      limit: 1,
      format: 'pretty',
    });

    console.log('RebuyEngine API response:', response);

    if (response.data && response.data.length > 0) {
      response.data.forEach((product) => {
        this.saveProductToCacheByID(product.id, product);
      });
      return response.data[0];
    } else {
      console.error('No product data returned from RebuyEngine API');
      return null;
    }
  }

  async getProductByHandle(handle, callback = () => {}) {
    const cachedProduct = this.getProductFromCacheByHandle(handle);

    if (cachedProduct) {
      return callback(cachedProduct);
    }

    const response = await this.query('api/v1/products/static', {
      metafields: 'yes',
      handles: handle,
      limit: 1,
    });

    response.data.forEach((product) => {
      this.saveProductToCacheByHandle(product.handle, product);
    });

    return callback(response.data[0]);
  }

  getProductHandle(path = window.location.pathname) {
    const handles = path.match(/\/products\/([^\/]*).*$/);
    return handles ? handles[1] : null;
  }

  getProductFromCacheByID(productID) {
    const cacheMax = Date.now() - this.maxCacheMinutes * 60 * 1000;
    const productJSON = JSON.parse(
      sessionStorage.getItem(`${this.storageKey}:${productID}`)
    );
    return productJSON && productJSON.cachedAt > cacheMax ? productJSON : null;
  }

  getProductFromCacheByHandle(handle) {
    const cacheMax = Date.now() - this.maxCacheMinutes * 60 * 1000;
    const productJSON = JSON.parse(
      sessionStorage.getItem(`${this.storageKey}:${handle}`)
    );
    return productJSON && productJSON.cachedAt > cacheMax ? productJSON : null;
  }

  saveProductToCacheByID(productID, productJSON) {
    productJSON.cachedAt = Date.now();
    sessionStorage.setItem(
      `${this.storageKey}:${productID}`,
      JSON.stringify(productJSON)
    );
  }

  saveProductToCacheByHandle(handle, productJSON) {
    productJSON.cachedAt = Date.now();
    sessionStorage.setItem(
      `${this.storageKey}:${handle}`,
      JSON.stringify(productJSON)
    );
  }

  removeProductFromCacheByID(productID) {
    sessionStorage.removeItem(`${this.storageKey}:${productID}`);
  }

  removeProductFromCacheByHandle(handle) {
    sessionStorage.removeItem(`${this.storageKey}:${handle}`);
  }
}
class ShopifyStorefrontClient extends RebuyClient {
  constructor({ store, apiAccessToken, apiVersion = '2025-07', clientToken }) {
    super({ clientToken });
    this.store = store;
    this.apiVersion = apiVersion;
    this.apiAccessToken = apiAccessToken;
  }

  async query(query, variables = {}) {
    try {
      const response = await fetch(
        `https://${this.store}/api/${this.apiVersion}/graphql.json`,
        {
          method: 'POST',
          body: JSON.stringify({ query, variables }),
          headers: {
            'Content-type': 'application/json; charset=UTF-8',
            'X-Shopify-Storefront-Access-Token': this.apiAccessToken,
          },
        }
      );

      const result = await response.json();

      if (result.errors) {
        console.error('GraphQL Errors:', result.errors);
        return null;
      }

      return result?.data;
    } catch (error) {
      console.error('API Request Error:', error);
      return null;
    }
  }

  async fetchWithCache(cacheKey, query, variables = {}) {
    const cached = sessionStorage.getItem(cacheKey);
    if (cached) {
      try {
        return JSON.parse(cached);
      } catch (err) {
        console.warn('Failed to parse cached data:', err);
      }
    }

    const data = await this.query(query, variables);

    sessionStorage.setItem(cacheKey, JSON.stringify(data));
    return data;
  }

  async getMetaobjectByIdCached(id) {
    const query = `
      query MetaobjectById($id: ID!) {
        node(id: $id) {
          ... on Metaobject {
            id
            type
            fields {
              key
              value
              type
              reference {
                ... on MediaImage {
                  id
                  image {
                    url
                    altText
                  }
                }
              }
            }
          }
        }
      }
    `;

    return this.fetchWithCache(`metaobject_${id}`, query, { id });
  }

  async getProductWithVideoMedia(id) {
    const query =
      'query ProductWithVideoMedia($id: ID!) { product(id: $id) { id title handle media(first: 50) { edges { node { mediaContentType ... on Video { id sources { url mimeType format height width } } ... on ExternalVideo { id host embeddedUrl } } } } } }';

    return this.fetchWithCache(`product_video_media_${id}`, query, { id });
  }

  async getProductVideoMediaOnly(id) {
    const query =
      'query ProductVideoMediaOnly($id: ID!) { product(id: $id) { id title handle media(first: 50) { edges { node { mediaContentType ... on Video { id sources { url mimeType format height width } } ... on ExternalVideo { id host embeddedUrl } } } } } }';
    return this.fetchWithCache(`product_video_only_${id}`, query, { id });
  }
  async getProductByGID(id) {
    const query = `query ProductById($id: ID!) {
      product(id: $id) {
        id
        title
        handle
        productType
        publishedAt
        createdAt
        updatedAt
        featuredImage {
          url
          id
        }
        variants(first: 250) {
          edges {
            node {
              id
              title
              price {
                amount
                currencyCode
              }
              compareAtPrice {
                amount
                currencyCode
              }
              sku
              taxable
              product {
                id
              }
              image {
                url
                id
              }
              selectedOptions {
                name
                value
              }
            }
          }
        }
      }
    }`;
    return this.fetchWithCache(`product_${id}`, query, { id });
  }
  async getProductVideoMediaFiltered(id) {
    const query =
      'query ProductVideoMediaFiltered($id: ID!) { product(id: $id) { id title handle media(first: 50) { edges { node { mediaContentType ... on Video { id sources { url mimeType format height width } } ... on ExternalVideo { id host embeddedUrl } } } } } }';

    return this.fetchWithCache(`product_video_filtered_${id}`, query, { id });
  }
  async getProductGroupingByTag(product) {
    const productTag = 'product-group::';

    let tagsArray;
    if (Array.isArray(product.tags)) {
      tagsArray = product.tags;
    } else {
      tagsArray = product.tags.split(',').map((tag) => tag.trim());
    }

    const groupingTag = tagsArray.find((tag) =>
      tag.toLowerCase().includes(productTag)
    );
    if (groupingTag) {
      const groupingBase = groupingTag.trim();
      const groupingBaseLower = groupingBase.toLowerCase();
      const searchQuery = `tag:"${groupingBaseLower}" OR tag:"${groupingBaseLower} ~"`;
      const query = `
        query ProductGroupingByTag($searchQuery: String!) {
          products(first: 50, query: $searchQuery) {
            nodes {
              id
              title
              featuredImage {
                url
              }
              media(first: 10) {
                nodes {
                  id
                  mediaContentType
                  alt
                  ... on MediaImage {
                    image {
                      url
                      altText
                      height
                      width
                    }
                  }
                }
              }
              tags
            }
          }
        }
      `;
      const cacheKey = `product_grouping_${groupingBaseLower.replace(
        /[^a-zA-Z0-9]/g,
        '_'
      )}`;
      return this.fetchWithCache(cacheKey, query, { searchQuery });
    }

    return null;
  }
}
const apiAccessToken = 'e6abc3438b95a4aa0a3c732c76cef7592837dda6';
const shopifyAccessToken = '790bd530acc3bbaca83296c0745dff73';
const shopifyStore = window.Shopify?.shop || 'oofos.myshopify.com';
const rebuyClient = new RebuyClient({
  clientToken: apiAccessToken,
});
const shopifyClient = new ShopifyStorefrontClient({
  store: shopifyStore,
  apiAccessToken: shopifyAccessToken,
  apiVersion: '2025-07',
});
const productHasGrouping = (product) => {
  if (product.tags) {
    return product.tags
      .split(',')
      .some((tag) => tag.toLowerCase().includes('product-group::'));
  }
  return false;
};

const extractProductId = (globalId) => {
  const match = globalId.match(/gid:\/\/shopify\/Product\/(\d+)/);
  return match ? match[1] : null;
};
const stitchRebuyProductsToWidget = async (product, idsArray) => {
  if (idsArray.length > 0) {
    const rebuyProducts = await rebuyClient.getProductsByIDs(idsArray);
    if (rebuyProducts.length > 0) {
      product.images = rebuyProducts.flatMap((product) => {
        return product.images.map((image, index) => {
          return {
            ...image,
            position: index,
          };
        });
      });
      product.variants = rebuyProducts.flatMap((product) => {
        return product.variants.map((variant) => {
          return {
            ...variant,
            title: product.title,
            image_id: product.image.id,
            image_url: product.image.src,
            metafields: product.metafields.reduce((acc, metafield) => {
              if (!acc[metafield.namespace]) {
                acc[metafield.namespace] = {};
              }
              acc[metafield.namespace][metafield.key] = metafield.value;
              return acc;
            }, {}),
          };
        });
      });
      product.options.find((option) => option.name === 'Color').values = [
        ...new Set(product.variants.map((variant) => variant.option2)),
      ];

      product.swatchColors = {};
      const colorOption = product.options.find(
        (option) => option.name.toLowerCase() === 'color'
      );
      if (colorOption && colorOption.values) {
        colorOption.values.forEach((value) => {
          if (window.theme && window.theme.colors) {
            const color = window.theme.colors.find((color) => {
              return color.color_names.includes(value);
            });
            if (color) {
              product.swatchColors[value] = {
                value: color.image ? color.image : color.color,
                image: color.image ? true : false,
              };
            } else {
              console.warn(`No color found for value: ${value}`);
            }
          } else {
            console.warn('window.theme.colors not available');
          }
        });
      }
      return product;
    }
  }
};
const stitchProductsToWidget = async (product) => {
  if (productHasGrouping(product)) {
    const productGrouping = await shopifyClient.getProductGroupingByTag(
      product
    );
    if (productGrouping) {
      const groupedProducts = productGrouping.products.nodes.map(
        (groupedProduct) => {
          return {
            ...groupedProduct,
            productId: extractProductId(groupedProduct.id),
          };
        }
      );
      const rebuyProductIds = groupedProducts.map((groupedProduct) => {
        return groupedProduct.productId;
      });
      await stitchRebuyProductsToWidget(product, rebuyProductIds);
      product.productGrouping = groupedProducts;
    }
  } else {
    product.productGrouping = [];
  }
};

const handleShowRemainingSwatches = (product, show) => {
  if (show) {
    // Clear any existing timeout
    if (product.showRemainingSwatchesTimeout) {
      clearTimeout(product.showRemainingSwatchesTimeout);
      product.showRemainingSwatchesTimeout = null;
    }
    product.showRemainingSwatches = true;
  } else {
    // Add a small delay before hiding to allow mouse movement
    product.showRemainingSwatchesTimeout = setTimeout(() => {
      product.showRemainingSwatches = false;
    }, 150);
  }
};

const handleDynamicBundleATC = (products) => {
  const selectedProducts = products.filter((p) => p.selected);
  const allProductsHaveSelectedVariant = selectedProducts.every(
    (p) => p.hasSelectedVariant
  );
  return selectedProducts.length > 0 && allProductsHaveSelectedVariant;
};
document.addEventListener('rebuy.productsChange', async (event) => {
  const widget = event.detail.widget;
  if (widget.data.products) {
    const processPromises = widget.data.products.map(async (product) => {
      await stitchProductsToWidget(product);
      if (
        product.hasSelectedVariant === undefined ||
        product.hasSelectedVariant === null ||
        product.hasSelectedVariant === true
      ) {
        product.hasSelectedVariant = false;
        product.showFullTitle = false;
      }
      return product;
    });

    await Promise.all(processPromises);

    // Reset all select elements to placeholder option
    setTimeout(() => {
      const selectElements = widget?.View?.vue?.$el.querySelectorAll(
        '.rebuy-select.variant-select'
      );
      selectElements.forEach((select) => {
        select.value = '';
        select.selectedIndex = 0;
      });
    }, 100);

    widget?.View?.vue?.$forceUpdate();
  }
});
